import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Icon from '../../Icons'
import {
  selectUserRole,
  selectUserData,
  selectUserBuilding,
} from '../../../reducers/userDataReducer'
import {
  selectBuildingsForFilter,
  formatBuildingsForFilter,
  setActiveBuilding,
  selectActiveBuilding,
} from '../../../reducers/buildingSelectorReducer'
import { selectClients, getClients } from '../../../reducers/clientsReducer'
import {
  selectPropertyManagers,
  fetchPropertyManagersByBuildingId,
} from '../../../reducers/Organisation/propertyManagersReducer'
import {
  selectShiftManagers,
  fetchShiftManagers,
} from '../../../reducers/Organisation/shiftManagersReducer'
import { fetchStaff, selectStaffPersons } from '../../../reducers/maintenanceStaffReducer'
import {
  dispatchRemoveAllUnreadConversationIds,
  dispatchSetConversations,
  selectUnreadConversationIds,
  selectConversations,
  dispatchSetLoadingConversations,
  dispatchGetChatContacts,
  selectChatContacts,
  dispatchAddClosedConversationIds,
  dispatchRemoveClosedConversationIds,
  dispatchAddAssignedConversationIds,
  dispatchRemoveAssignedConversationIds,
  selectClosedConversationsData,
  selectAssignedConversationsData,
  dispatchSetLoadingMessages,
  dispatchGetChatInboxes,
  getInvoicesWithCount,
  dispatchSetHighManagementUsers,
  selectHighManagementUsers,
  dispatchGetCustomChatInbox,
} from '../../../reducers/chatReducer'
import {
  updateChatAssigneeFirebase,
  getAllConversationsPerBuildingId,
} from '../../../firebase/privateChat/firebaseActions'
import API from '../../../api/api'
import { TwilioService } from '../services/twilio-service'
import useChatGeneral from '../useChatGeneral'
import useChatListenerClosedConv from '../useChatListenerClosedConv'
import useChatListenerAssignedConv from '../useChatListenerAssignedConv'
import { hasArrayEntries, getChatFeedFlagsDueToRole, getCookie } from '../../../helpers/helpers'
import {
  getProperStaffUsers,
  getChatTypeFromConversation,
  getProperTeamNameByType,
} from '../helpers'

import ChatContactsWindowForWidget from './ChatContactsWindowForWidget/ChatContactsWindowForWidget'
import SearchAndSelectWindow from './SearchAndSelectWindow/SearchAndSelectWindow'
import ConversationWithTab from './Tabs/ConversationWithTab'
import ConversationsTab from './Tabs/ConversationsTab'
import InboxTab from './Tabs/InboxTab'
import HomeTab from './Tabs/HomeTab'

const ChatWidget = ({
  clients,
  userRole,
  userData,
  buildings,
  currentBuildingId,
  setActiveBuilding,
  dispatchRemoveAllUnreadConversationIds,
  dispatchSetConversations,
  unreadConversationIds,
  conversations,
  dispatchSetLoadingConversations,
  dispatchGetChatContacts,
  chatContacts,
  fetchPropertyManagersByBuildingId,
  fetchShiftManagers,
  fetchStaff,
  closedConversationsData,
  assignedConversationData,
  dispatchAddClosedConversationIds,
  dispatchRemoveClosedConversationIds,
  dispatchAddAssignedConversationIds,
  dispatchRemoveAssignedConversationIds,
  dispatchSetLoadingMessages,
  shiftManagers,
  propertyManagers,
  staffPeople,
  getClients,
  dispatchGetChatInboxes,
  getInvoicesWithCount,
  dispatchSetHighManagementUsers,
  highManagementUsers,
  dispatchGetCustomChatInboxes,
}) => {
  const { t } = useTranslation(['chat'])
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedTab, selectTab] = useState('home')
  const [selectedInbox, setInbox] = useState(null)
  const selectedInboxType = selectedInbox?.type ?? 'ALL'
  const [conversationsFilter, setConversationsFilter] = useState(null)
  const [assigneeWindowVisible, setAssigneeWindowVisible] = useState(false)
  const [findChatWindowVisible, setFindChatWindowVisible] = useState(false)
  const [converationData, setConverationData] = useState(null)

  const [isWidgetVisible, setWidgetVisible] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [noChatForBuildings, setNoChatForBuildings] = useState(false)

  const [filteredBuildings, setFilteredBuildings] = useState(buildings)
  const [selectedBuilding, setSelectedBuilding] = useState(null)

  const isAuthorizedToken = getCookie('token')
  const managementUsers = [...propertyManagers, ...shiftManagers, ...staffPeople]
  const isActiveTab = (tabName) => selectedTab === tabName
  const isAdminOrPM = ['Administrator', 'PropertyManager'].includes(userRole)
  const availableManagementUsers = getProperStaffUsers(selectedInboxType, managementUsers)

  const isOpenSearchConversationsWindow =
    findChatWindowVisible && selectedInbox && !['PMT', 'FDC'].includes(selectedInboxType)
  const isOpenCreateConversationsWindow =
    findChatWindowVisible && selectedInbox && ['PMT', 'FDC'].includes(selectedInboxType)

  useEffect(() => {
    dispatchRemoveAllUnreadConversationIds()
  }, [currentBuildingId])

  useChatGeneral({
    userData,
    selectedBuilding,
    conversations,
    chatContacts,
    dispatchSetConversations,
    dispatchSetLoadingConversations,
    dispatchGetChatContacts,
    dispatchGetChatInboxes,
    getInvoicesWithCount,
    getAllConversationsPerBuildingId,
    dispatchGetCustomChatInboxes,
  })

  useChatListenerClosedConv({
    userRole,
    selectedBuilding,
    dispatchAddClosedConversationIds,
    dispatchRemoveClosedConversationIds,
  })

  useChatListenerAssignedConv({
    selectedBuilding,
    dispatchAddAssignedConversationIds,
    dispatchRemoveAssignedConversationIds,
  })

  const handleOpenInbox = (inbox, filter) => {
    if (inbox) {
      setInbox(inbox)
      selectTab('conversations')
      if (filter) {
        setConversationsFilter(filter)
      }
    } else {
      selectTab('inbox')
      setInbox(null)
    }
  }

  const handleOpenConversation = (conversationData) => {
    dispatchSetLoadingMessages(true)
    setConverationData(conversationData)
    selectTab('conversationWith')
  }

  const renderProperView = () => {
    switch (selectedTab) {
      case 'home':
        return <HomeTab />
      case 'inbox':
        return (
          <InboxTab
            userData={userData}
            selectedBuilding={selectedBuilding}
            buildings={filteredBuildings}
            unreadConversationIds={unreadConversationIds}
            handleClose={() => setIsOpen(false)}
            selectBuilding={selectBuilding}
            handleOpenInbox={handleOpenInbox}
          />
        )
      case 'conversations':
        return (
          <ConversationsTab
            selectedBuilding={selectedBuilding}
            inbox={selectedInbox}
            conversationsFilter={conversationsFilter}
            currentUser={userData}
            closedConversationsData={closedConversationsData}
            assignedConversationData={assignedConversationData}
            availableManagementUsers={availableManagementUsers}
            highManagementUsers={highManagementUsers}
            showAssigneeWindowForConversation={(conversation) => {
              setAssigneeWindowVisible(true)
              setConverationData(conversation)
            }}
            showFindChatWindow={() => setFindChatWindowVisible(true)}
            handleBack={() => handleOpenInbox()}
            handleOpenConversation={handleOpenConversation}
            handleClose={() => setIsOpen(false)}
          />
        )
      case 'conversationWith':
        return (
          <ConversationWithTab
            selectedChat={converationData}
            currentUser={userData}
            selectedBuilding={selectedBuilding}
            closedConversationsData={closedConversationsData}
            assignedConversationData={assignedConversationData}
            availableManagementUsers={availableManagementUsers}
            highManagementUsers={highManagementUsers}
            showAssigneeWindowForConversation={(conversation) => {
              setAssigneeWindowVisible(true)
              setConverationData(conversation)
            }}
            handleBack={() => selectTab('conversations')}
            handleClose={() => setIsOpen(false)}
          />
        )
      default:
        return null
    }
  }

  const handleAssignUser = (userData) => {
    setAssigneeWindowVisible(false)
    updateChatAssigneeFirebase(currentBuildingId, converationData.channelName, userData.id)
  }

  const onOpenChat = (data) => {
    const conversationToOpen = conversations.find(
      (conversation) => conversation.channelName === data.channelName,
    )
    handleOpenConversation(conversationToOpen)
    setFindChatWindowVisible(false)
  }

  const onCreateOrJoin = (userData) => {
    const dataAboutChat = {
      teamType: selectedInboxType,
      conversationUserId: userData.id,
    }

    if (selectedInboxType === 'CUST') {
      dataAboutChat.customInboxId = selectedInbox.id
    }

    API.createOrJoinConversation(dataAboutChat)
      .then(async (conversation) => {
        const newConversation = TwilioService.getInstance().parseConversation(conversation)
        const convertedConversation = {
          ...newConversation,
          channelName: newConversation.name,
          avatar: userData.avatar,
          name: `${userData.firstName} ${userData.lastName}`,
          role: userData.role,
          email: userData.email,
          phone: userData.phone,
          apartment: userData.apartment,
          address: `${userData.street} #${userData.apartment}`,
        }

        await dispatchSetConversations(conversations.concat(convertedConversation))
        handleOpenConversation(convertedConversation)
        setFindChatWindowVisible(false)
      })
      .catch((err) => {
        console.error(err.message)
      })
  }

  const fetchContacts = async (searchingQuery, isScrolling) => {
    const skipMore =
      clients.length % 15 === 0
        ? clients.length / 15
        : (clients.length - (clients.length % 15)) / 15
    if (typeof searchingQuery === 'undefined') {
      try {
        await getClients(skipMore + 1, currentBuildingId, userData.user.id)
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery !== '' && isScrolling) {
      try {
        await getClients(skipMore + 1, currentBuildingId, userData.user.id, searchingQuery)
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery !== '' && !isScrolling) {
      try {
        await getClients(
          searchingQuery ? 1 : skipMore + 1,
          currentBuildingId,
          userData.user.id,
          searchingQuery,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery === '' && isScrolling) {
      try {
        await getClients(skipMore + 1, currentBuildingId, userData.user.id)
      } catch (err) {
        throw new Error(err)
      }
    } else {
      try {
        await getClients(1, currentBuildingId, userData.user.id)
      } catch (err) {
        throw new Error(err)
      }
    }
  }

  const getUsersFromConversations = () => {
    if (!conversations.length) return []

    const filteredConversations = [
      ...conversations
        .filter((conversation) => conversation.lastMessage)
        .filter((conversation) => {
          if (selectedInbox && ['PMT', 'FDC', 'MT'].includes(selectedInboxType)) {
            return selectedInboxType === getChatTypeFromConversation(conversation)
          }
          return true
        }),
    ]

    const userArray = filteredConversations.map((conversation) => {
      return {
        avatar: conversation.avatar,
        firstName: conversation.name.split(' ')[0],
        lastName: conversation.name.split(' ')[1],
        chatType: getProperTeamNameByType(getChatTypeFromConversation(conversation)),
        channelName: conversation.channelName,
      }
    })

    userArray.sort((a, b) => {
      if (a.lastName > b.lastName) return -1
      if (a.lastName < b.lastName) return 1
      return 0
    })

    return userArray
  }

  const selectBuilding = (building) => {
    setActiveBuilding(building)
  }

  useEffect(() => {
    if (isAuthorizedToken) {
      API.getHighManagementUsers().then((response) => {
        dispatchSetHighManagementUsers(response)
      })
    }
  }, [isAuthorizedToken])

  useEffect(() => {
    if (isAuthorizedToken && userRole !== 'Client' && buildings.length) {
      ;(async () => {
        const buildingList = formatBuildingsForFilter(buildings)
        const feedFlags = [...getChatFeedFlagsDueToRole(userRole), 'widgetChatAvailable']
        const filteredBuildingsList =
          userRole === 'Administrator'
            ? buildingList
            : buildingList.filter((someBuilding) => feedFlags.every((flag) => someBuilding[flag]))
        setFilteredBuildings(filteredBuildingsList)

        if (!filteredBuildingsList.length) {
          setNoChatForBuildings(true)
        } else {
          setNoChatForBuildings(false)
        }

        const thisBuildingId =
          currentBuildingId ||
          (hasArrayEntries(filteredBuildingsList) ? filteredBuildingsList[0].id : undefined)

        if (currentBuildingId !== String(thisBuildingId)) {
          const buildingToSelect = buildings.find((x) => x.id === thisBuildingId)
          setActiveBuilding(buildingToSelect)
        }
      })()
    }
  }, [isAuthorizedToken, userRole, buildings.length])

  useEffect(() => {
    if (!currentBuildingId) return
    if (['Administrator', 'Manager', 'PropertyManager'].includes(userRole)) {
      fetchPropertyManagersByBuildingId(currentBuildingId)
      fetchShiftManagers(currentBuildingId)
      fetchStaff(currentBuildingId)
    }
    if (userRole === 'ShiftManager') {
      fetchPropertyManagersByBuildingId(currentBuildingId)
      fetchShiftManagers(currentBuildingId)
    }
    if (userRole === 'Staff') {
      fetchPropertyManagersByBuildingId(currentBuildingId)
      fetchStaff(currentBuildingId)
    }
    fetchContacts('')
  }, [userRole, currentBuildingId])

  useEffect(() => {
    setSelectedBuilding(
      buildings.find((building) => String(building.id) === String(currentBuildingId)),
    )
  }, [currentBuildingId, buildings.length])

  useEffect(() => {
    const isParkingGuestApp = /\/parking\/\d\/\d/.test(location.pathname)
    const isBuildingAvailableForWidget = filteredBuildings.find(
      (building) => String(building.id) === String(currentBuildingId),
    )
    if (
      location.pathname.includes('/chat') ||
      !getCookie('token') ||
      userRole === 'Client' ||
      !isBuildingAvailableForWidget ||
      isParkingGuestApp
    ) {
      setWidgetVisible(false)
      setIsOpen(false)
    } else {
      setWidgetVisible(true)
    }
  }, [location.pathname, userRole, noChatForBuildings, currentBuildingId, filteredBuildings.length])

  useEffect(() => {
    if (isOpen) {
      dispatchSetLoadingMessages(true)
    }
  }, [isOpen])

  if (!isWidgetVisible || noChatForBuildings) return null
  return (
    <div className="chat-widget-main-container">
      {isOpen && (
        <div className="widget-inner-container">
          {renderProperView()}
          <div className="widget-footer">
            <div
              className={`footer-button ${isActiveTab('home') ? 'is-active' : ''}`}
              onClick={() => selectTab('home')}
            >
              <Icon icon="home" viewBox="0 0 21 21" />
              <span>{t('chat:widget.home')}</span>
            </div>
            <div
              className={`footer-button ${isActiveTab('inbox') ? 'is-active' : ''}`}
              onClick={() => selectTab('inbox')}
            >
              <Icon icon="inbox" viewBox="0 0 24 24" />
              <span>{t('chat:widget.inbox')}</span>
            </div>
            {isAdminOrPM && (
              <div
                className="footer-button"
                onClick={() => navigate('/dashboard/chat?settings=true')}
              >
                <Icon icon="settings" viewBox="0 0 24 24" />
                <span>{t('chat:widget.settings')}</span>
              </div>
            )}
          </div>
          <SearchAndSelectWindow
            title={t('chat:widget.assign')}
            isOpen={assigneeWindowVisible}
            data={availableManagementUsers}
            onSelectAction={handleAssignUser}
            onClose={() => setAssigneeWindowVisible(false)}
          />
          <SearchAndSelectWindow
            title={t('chat:widget.conversationWith')}
            isOpen={isOpenSearchConversationsWindow}
            showChatType={true}
            data={getUsersFromConversations()}
            onSelectAction={onOpenChat}
            onClose={() => setFindChatWindowVisible(false)}
          />
          <ChatContactsWindowForWidget
            isOpen={isOpenCreateConversationsWindow}
            onClose={() => setFindChatWindowVisible(false)}
            fetchContacts={fetchContacts}
            onSelectAction={onCreateOrJoin}
          />
        </div>
      )}
      <div className="widget-shortcut" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? (
          <Icon icon="arrow" viewBox="0 2 16 16" styles={{ rotate: '90deg' }} />
        ) : (
          <Icon icon="message" viewBox="5 3 23 24" />
        )}
        {unreadConversationIds.length ? (
          <div className="chat-notification-dot">{unreadConversationIds.length}</div>
        ) : null}
      </div>
    </div>
  )
}

ChatWidget.propTypes = {
  router: PropTypes.object,
  userRole: PropTypes.string,
  userData: PropTypes.object,
  buildings: PropTypes.array,
  currentBuildingId: PropTypes.number,
  setActiveBuilding: PropTypes.func,
  dispatchRemoveAllUnreadConversationIds: PropTypes.func,
  dispatchSetConversations: PropTypes.func,
  unreadConversationIds: PropTypes.array,
  conversations: PropTypes.array,
  dispatchSetLoadingConversations: PropTypes.func,
  dispatchGetChatContacts: PropTypes.func,
  chatContacts: PropTypes.array,
  closedConversationsData: PropTypes.array,
  assignedConversationData: PropTypes.arrayOf(PropTypes.object),
  fetchPropertyManagersByBuildingId: PropTypes.func,
  fetchShiftManagers: PropTypes.func,
  fetchStaff: PropTypes.func,
  dispatchAddClosedConversationIds: PropTypes.func,
  dispatchRemoveClosedConversationIds: PropTypes.func,
  dispatchAddAssignedConversationIds: PropTypes.func,
  dispatchRemoveAssignedConversationIds: PropTypes.func,
  dispatchSetLoadingMessages: PropTypes.func,
  shiftManagers: PropTypes.array,
  propertyManagers: PropTypes.array,
  staffPeople: PropTypes.array,
  clients: PropTypes.array,
  getClients: PropTypes.func,
  dispatchGetChatInboxes: PropTypes.func,
  getInvoicesWithCount: PropTypes.func,
  dispatchSetHighManagementUsers: PropTypes.func,
  highManagementUsers: PropTypes.array,
  dispatchGetCustomChatInboxes: PropTypes.func,
}

export default connect(
  (state) => {
    const userRole = selectUserRole(state)
    const currentBuilding = selectActiveBuilding(state)
    return {
      userRole,
      userData: selectUserData(state),
      clients: selectClients(state),
      currentBuildingId: currentBuilding ? currentBuilding.id : null,
      buildings: ['ShiftManager', 'Staff'].includes(userRole)
        ? formatBuildingsForFilter([selectUserBuilding(state)])
        : selectBuildingsForFilter(state),
      unreadConversationIds: selectUnreadConversationIds(state),
      conversations: selectConversations(state),
      chatContacts: selectChatContacts(state),
      closedConversationsData: selectClosedConversationsData(state),
      assignedConversationData: selectAssignedConversationsData(state),
      shiftManagers: selectShiftManagers(state),
      propertyManagers: selectPropertyManagers(state),
      staffPeople: selectStaffPersons(state),
      highManagementUsers: selectHighManagementUsers(state),
    }
  },
  {
    setActiveBuilding,
    dispatchRemoveAllUnreadConversationIds,
    dispatchSetConversations,
    dispatchSetLoadingConversations,
    dispatchGetChatContacts,
    fetchPropertyManagersByBuildingId,
    fetchShiftManagers,
    fetchStaff,
    getClients,
    dispatchAddClosedConversationIds,
    dispatchRemoveClosedConversationIds,
    dispatchAddAssignedConversationIds,
    dispatchRemoveAssignedConversationIds,
    dispatchSetLoadingMessages,
    dispatchGetChatInboxes,
    getInvoicesWithCount,
    dispatchSetHighManagementUsers,
    dispatchGetCustomChatInboxes: dispatchGetCustomChatInbox,
  },
)(ChatWidget)
